import { Access } from "@p0security/shared/types/access/index";

export const OnePasswordAccesses = {
  item: Access({
    delegation: {},
    description: "A read-only share link to one item in a 1Password vault",
    // Filled in at stage, after approval, when P0 makes the link.
    generated: {
      link: {
        description:
          "The share link. It opens only for the requestor's email, which 1Password verifies with a code.",
        label: "Share link",
        type: "string",
      },
      expiresAt: {
        description:
          "When 1Password stops opening the link: the approved duration, rounded to one of 1Password's options",
        label: "Link expires",
        type: "string",
      },
      oneTimeLink: {
        choices: ["yes", "no"],
        description:
          "Whether the link opens only once, from the account's one-time links setting when the link was made",
        label: "Opens once",
        type: "string",
      },
    },
    label: "Item",
    maturity: "beta",
    parameters: {
      // Chosen from the installed accounts, which the request form lists in
      // full. The backend's commands select a lone one.
      account: {
        description: "1Password account, by its sign-in address",
        label: "Account",
        type: "static",
      },
      // Chosen from the vaults the account's service account can see, which
      // the connector lists. An account has few, so the form lists them all
      // once the account is chosen, and the web form selects a lone one.
      // Recorded by its ID.
      vault: {
        description: "1Password vault",
        isPreselect: true,
        label: "Vault",
        type: "static",
      },
      // Chosen from the vault's items, which the connector lists.
      item: {
        description: "Item to share",
        label: "Item",
        type: "dynamic",
      },
      // Resolved from `vault` and `item` when the request is made, so that
      // rules can filter on them before approval, and the approver sees which
      // vault and item it is.
      vaultName: {
        description: "The vault's name in 1Password when it was requested",
        label: "Vault name",
        type: "generated",
      },
      itemId: {
        description: "The item's ID in 1Password",
        label: "Item ID",
        type: "generated",
      },
      itemTitle: {
        description: "The item's title in 1Password when it was requested",
        label: "Item title",
        type: "generated",
      },
      itemType: {
        description: "The item's category in 1Password, such as Login",
        label: "Item type",
        type: "generated",
      },
    },
    render: {
      // The vault's name and the item's title, as they were when the request
      // was made, rather than their IDs.
      locator: "account",
      resource: "vaultName",
      policy: "itemTitle",
      // Follows the line saying the access was granted, after a blank line.
      // The item's title is bold, in the markup the request's own line uses.
      // Typed by hand: inferring it from this access would be circular.
      instructions: ({
        generated,
        permission,
      }: {
        generated: { link?: string; expiresAt?: string; oneTimeLink?: string };
        permission: { itemTitle?: string };
      }) =>
        generated.link
          ? `\nOpen *${permission.itemTitle}* in 1Password: ${generated.link}. Link valid until ${generated.expiresAt}.${generated.oneTimeLink === "yes" ? " The link opens only once, so copy what you need when you open it." : ""}`
          : undefined,
    } as const,
  }),
};

export type OnePasswordAccesses = typeof OnePasswordAccesses;
export type OnePasswordItemAccess = OnePasswordAccesses["item"];
